import { Injectable } from '@angular/core';
import { User } from 'src/interfaces/user.interface';
import { UserLogInResponse as PunchhUserLoginResponse } from './interfaces/user-log-in-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { DineEngineError } from 'src/interfaces/dineengine-error.interface';
import { HistoryEvent } from 'src/interfaces/history-event.interface';
import { AccountHistoryItem } from './interfaces/account-history-item.interface';
import { Redeemable } from './interfaces/redeemable.interface';
import { Reward } from './interfaces/reward.interface';
import { LoyaltyReward } from '../../interfaces/loyalty-reward.interface';
import moment from 'moment-timezone';
import { Message } from './interfaces/mobile-fetch-user-balance.interface';
import { InboxMessage } from '../../interfaces/inbox-message.interface';
import { LoyaltyLocation } from '../../interfaces/location.interface';
import { PunchhLocation } from './interfaces/get-meta-response.interface';
import { Offer, OfferType, OfferRedemptionDetails } from '../../interfaces/offer.interface';
import {
  MobileOffersResponse,
  MobileOfferReward,
  MobileOfferCoupon,
  MobileOfferNotification,
} from './interfaces/mobile-offers-response.interface';

@Injectable({
  providedIn: 'root',
})
export class PunchhMappingService {
  toUser(user: PunchhUserLoginResponse, loyaltyLocations: LoyaltyLocation[]): User {
    return {
      firstName: user.first_name,
      lastName: user.last_name,
      phoneNumber: user.phone,
      email: user.email,
      userID: user.id.toString(),
      orderingToken: user.authentication_token,
      userAsBarcode: user.user_as_barcode,
      userAsQrCode: user.user_as_qrcode,
      orderingTokenProvider: 'punchh',
      favoriteLocation: user.favourite_locations ? loyaltyLocations.find(l => l.locationID === user.favourite_locations) : null,
      birthday: user.birthday ? moment(user.birthday).toDate() : null,
      providerSpecificFields: user,
    } as unknown as User;
  }

  accountHistoryToHistoryEvents(punchhHistory: AccountHistoryItem[]): HistoryEvent[] {
    return punchhHistory.map(t => {
      const eventValue = t.event_value;
      const dollarCheck = eventValue.substring(1, eventValue.length - 1);
      let rewardValue = 0;
      if (!eventValue.includes('Item')) {
        if (eventValue.charAt(0) === '+' && eventValue.substring(eventValue.length - 6, eventValue.length) !== 'points') {
          // console.log(eventValue, eventValue.substring(2, eventValue.length), parseFloat(eventValue.substring(2, eventValue.length)));
          // earnedValue = parseFloat(eventValue.substring(2, eventValue.length - 1));
        } else if (dollarCheck.charAt(0) === '$') {
          rewardValue = parseFloat(eventValue.substring(2, eventValue.length - 1));
        }
      }

      const historyEvent: HistoryEvent = {
        type: t.event_name,
        title: t.title,
        description: t.description,
        pointsEarned: null,
        pointsOff: null,
        dollarsEarned: null,
        dollarsOff: rewardValue,
        timeStamp: t.date,
        externalID:
          t.event_name === 'Redemption' && t.event_details && t.event_details?.redeemable_id ? t.event_details.redeemable_id : null,
        redemptionInfo:
          t.event_name === 'Redemption' && t.event_details
            ? {
                redemptionCode: t.event_details.internal_tracking_code,
                redemptionMessage: t.event_details.redemption_message,
                expiryDate: t.event_details.expiring_on,
              }
            : null,
      };
      return historyEvent;
    });
  }

  loginError(err: Error): DineEngineError {
    let msg = err.message;
    if (err instanceof HttpErrorResponse) {
      let defMsg = 'Error logging in. Please check your username and password and try again.';
      if (err && err.error) {
        defMsg += ' Message from server: ' + err.error.error;
      }
      switch (err.status) {
        case 401:
          msg = err.error.error;
          break;
        case 503:
          msg = defMsg;
          break;
      }
    }
    return new DineEngineError(msg);
  }

  createAccountError(error: Error): DineEngineError {
    let msg = error.message;
    if (error instanceof HttpErrorResponse) {
      let defMsg = '';
      Object.keys(error.error.errors).forEach(key => {
        defMsg = defMsg.concat(`${key.toUpperCase()} ${error.error.errors[key].join(' ,')}. `);
      });
      msg = defMsg;
    }
    return new DineEngineError(msg);
  }

  barCodeErr(err: Error): DineEngineError {
    let msg = '';
    if (err instanceof HttpErrorResponse) {
      switch (err.status) {
        case 422:
          {
            if (err.error && err.error.errors && err.error.errors.base) {
              let errors = err.error.errors.base;
              if (!Array.isArray(errors)) {
                errors = [];
              }
              msg = errors.join(', ');
            }
          }
          break;
        case 412:
          {
            if (err.error && err.error.errors && err.error.errors.invalid_signature) {
              let errors = err.error.errors.invalid_signature;
              if (!Array.isArray(errors)) {
                errors = [];
              }
              msg = errors.join(', ');
            }
          }
          break;
        case 400:
          {
            if (err.error && err.error.errors && err.error.errors.missing_required_params) {
              let errors = err.error.errors.missing_required_params;
              if (!Array.isArray(errors)) {
                errors = [];
              }
              msg = errors.join(', ');
            }
          }
          break;
        default:
          msg = 'There was an issue processing your receipt.';
      }
    }
    return new DineEngineError(msg);
      }

  redeemableToLoyaltyReward(redeemable: Redeemable, rewards?: Reward[]): LoyaltyReward {
    // Check if there's a matching reward with the same redeemable_id
    let expDate = redeemable.expiry_date ? moment(redeemable.expiry_date).toDate() : null;
    
    if (rewards && rewards.length > 0) {
      const matchingReward = rewards.find(reward => reward.redeemable_id === redeemable.redeemable_id);
      if (matchingReward && matchingReward.end_date_tz) {
        expDate = moment(matchingReward.end_date_tz).toDate();
      }
    }

    return <LoyaltyReward>{
      name: redeemable.name,
      description: redeemable.description,
      imageURL: redeemable.image,
      pointCost: redeemable.points,
      expDate: expDate,
      externalReferenceID: String(redeemable.redeemable_id),
    };
  }

  messageToInboxMessage(message: Message): InboxMessage {
    return <InboxMessage>{
      messageID: message.message_id.toString(),
      date: moment(message.created_at).toDate(),
      title: message.title,
      body: message.body,
      imageURL: message.hero_content?.url,
      read: !!message.read_at,
    };
  }

  punchhLocationToLoyaltyLocation(punchhLocation: PunchhLocation): LoyaltyLocation {
    return {
      locationID: punchhLocation.location_id.toString(),
      name: punchhLocation.name,
      address: {
        address1: punchhLocation.address,
        address2: null,
        city: punchhLocation.city,
        state: punchhLocation.state,
        zipCode: punchhLocation.post_code,
        country: punchhLocation.country,
        latitude: Number(punchhLocation.latitude),
        longitude: Number(punchhLocation.longitude),
      },
    };
  }

  mobileOffersResponseToOffers(mobileOffersResponse: MobileOffersResponse): Offer[] {
    const offers: Offer[] = [];

    // Convert rewards to offers
    if (mobileOffersResponse.rewards) {
      mobileOffersResponse.rewards.forEach(reward => {
        offers.push(this.mobileRewardToOffer(reward));
      });
    }

    // Convert coupons to offers
    if (mobileOffersResponse.coupons) {
      mobileOffersResponse.coupons.forEach(coupon => {
        offers.push(this.mobileCouponToOffer(coupon));
      });
    }

    // Convert notifications to offers
    if (mobileOffersResponse.notifications) {
      mobileOffersResponse.notifications.forEach(notification => {
        offers.push(this.mobileNotificationToOffer(notification));
      });
    }

    return offers;
  }

  private mobileRewardToOffer(reward: MobileOfferReward): Offer {
    const redemptionDetails: OfferRedemptionDetails | undefined = reward.redemption_details ? {
      redemptionId: reward.redemption_details.redemption_id,
      redemptionStatus: reward.redemption_details.redemption_status,
      redemptionTrackingCode: reward.redemption_details.redemption_tracking_code,
      redemptionMessage: reward.redemption_details.redemption_message,
      redeemedValue: reward.redemption_details.redeemed_value,
      expiringAt: moment(reward.redemption_details.expiring_at).toDate(),
    } : undefined;

    return {
      id: reward.reward_id,
      name: reward.name,
      description: reward.description,
      couponCode: undefined,
      expireDate: moment(reward.expiring_at).toDate(),
      availableDate: moment(reward.becomes_available_at).toDate(),
      imageUrl: reward.reward_image_url,
      redeemInStore: reward.discount_channel !== 'online_only',
      redeemOnline: reward.discount_channel !== 'offline_only',
      offerType: OfferType.REWARD,
      isRead: !!reward.read_at,
      autoSelect: reward.auto_select,
      discountChannel: reward.discount_channel,
      discountAmount: reward.discount_amount,
      storeNumbers: reward.store_numbers,
      properties: reward.reward_properties,
      metaData: reward.meta_data,
      redemptionDetails,
    };
  }

  private mobileCouponToOffer(coupon: MobileOfferCoupon): Offer {
    return {
      id: coupon.code.hashCode(), // Generate ID from coupon code since no ID provided
      name: coupon.name,
      description: coupon.description || '',
      couponCode: coupon.code,
      expireDate: coupon.end_date ? moment(coupon.end_date).toDate() : moment().add(1, 'year').toDate(),
      availableDate: coupon.start_date ? moment(coupon.start_date).toDate() : moment().toDate(),
      imageUrl: coupon.image_url,
      redeemInStore: true,
      redeemOnline: true,
      offerType: OfferType.COUPON,
      isRead: false, // Coupons don't have read status in the API
      autoSelect: false,
      discountChannel: 'all',
      storeNumbers: [],
      properties: '',
      metaData: '',
    };
  }

  private mobileNotificationToOffer(notification: MobileOfferNotification): Offer {
    return {
      id: notification.id,
      name: notification.kind,
      description: notification.message,
      couponCode: undefined,
      expireDate: moment().add(30, 'days').toDate(), // Default expiry for notifications
      availableDate: moment(notification.created_at).toDate(),
      imageUrl: undefined,
      redeemInStore: false,
      redeemOnline: false,
      offerType: OfferType.NOTIFICATION,
      isRead: !!notification.read_at,
      autoSelect: false,
      storeNumbers: [],
      properties: '',
      metaData: '',
    };
  }
}

// Extension to String for generating hash codes from coupon codes
declare global {
  interface String {
    hashCode(): number;
  }
}

String.prototype.hashCode = function(): number {
  let hash = 0;
  if (this.length === 0) return hash;
  for (let i = 0; i < this.length; i++) {
    const char = this.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};
